"use client";

import { ChevronRight } from "lucide-react";
import { motion } from "motion/react";
import { AuthorAvatars, type AuthorChip } from "@/components/widget/author-avatars";
import { toExcerptText } from "@/lib/excerpt-text";
import { cn } from "@/lib/utils";

/**
 * What this row represents. Drives typography differences — no leading
 * icons (they added clutter and we don't need them when the type
 * differences are visible in weight + density alone).
 *
 *   - `folder`: bold title, regular description, small meta line for
 *     the article count → reads as a navigational header.
 *   - `article`: regular-weight title, lighter description, no meta
 *     line → reads as a list item.
 *   - `none`: same as folder (default for the root collections list).
 */
export type HelpListRowKind = "folder" | "article" | "none";

/**
 * Full-width, divider-separated row used by the Help tab and category
 * drill-in views. Help-center-style list — folder rows render bold with
 * a description + count meta; article rows render lighter and more
 * compact (regular-weight title, smaller description), so a mixed
 * list scans naturally as "headings > items" without needing icons.
 *
 * Hover background extends edge-to-edge (no horizontal padding around
 * the button itself) so the visitor's tap target matches the
 * highlight; the inner padding lives on the row's flex container.
 */
export function HelpListRow({
  title,
  description,
  meta,
  authors,
  onClick,
  kind = "none",
  delay = 0,
}: {
  title: string;
  description?: string | null;
  /** Small grey line under the description — used for "Articles: 33"
   *  on collection / subfolder rows. Renders nothing when omitted.
   *  Article rows intentionally don't pass it. */
  meta?: string;
  /** Up to a few author avatars on the right. Renders before the
   *  chevron when present. */
  authors?: AuthorChip[];
  onClick: () => void;
  kind?: HelpListRowKind;
  delay?: number;
}) {
  const isArticle = kind === "article";
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, delay }}
      className={cn(
        "group flex w-full items-center gap-3 border-wx-border border-b text-start transition-colors",
        "hover:bg-wx-bg-elevated/60",
        "last:border-b-0",
        // Article rows are visually denser — tighter vertical rhythm
        // reinforces "list item" vs. the folder rows' "section heading"
        // feel.
        isArticle ? "px-5 py-3" : "px-5 py-3.5",
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 [overflow-wrap:anywhere]">
        <span
          className={cn(
            "text-wx-fg",
            // Folders / collections read as navigation: bold title at
            // the standard small body size. Articles read as content
            // rows: lighter weight, same size, looks list-y.
            isArticle ? "font-normal text-sm" : "font-semibold text-sm",
          )}
        >
          {title}
        </span>
        {description && (
          <span
            className={cn(
              "leading-snug",
              // Article excerpts are deliberately dimmer + smaller so
              // the whole row reads as a single "item" with quiet
              // supporting text; folder descriptions stay at body size
              // so the section header still has weight.
              isArticle ? "truncate text-[11px] text-wx-fg-subtle" : "text-wx-fg-muted text-xs",
            )}
          >
            {toExcerptText(description, 160)}
          </span>
        )}
        {meta && <span className="text-[11px] text-wx-fg-subtle">{meta}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-2 text-wx-fg-muted">
        {authors && authors.length > 0 && <AuthorAvatars authors={authors} />}
        <ChevronRight
          size={16}
          className="transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5"
          aria-hidden="true"
        />
      </span>
    </motion.button>
  );
}
